import {
  InboxOutlined,
  InfoCircleOutlined,
  PaperClipOutlined
} from "@ant-design/icons";
import {
  Button,
  Drawer,
  message,
  Spin,
  Upload,
  type UploadFile,
  type UploadProps
} from "antd";
import Dragger from "antd/lib/upload/Dragger";
import { useEffect, useMemo, useState } from "react";
import "./bootstrap-design-scope.scss";
import {
  catalogImportHasRowFailures,
  catalogImportSummaryStats,
  maybeDownloadImportFailureReport,
  type DesignScopeImportLogStats
} from "./import-log-result";
import ImportLogSummaryPanel from "./import-log-summary-panel";
import { useBootstrapDesignScopeApis } from "./use-bootstrap-design-scope-apis";
import {
  DESIGN_SCOPE_CSV_FILE_ERROR,
  DESIGN_SCOPE_ITEMS_IMPORTED
} from "./design-scope-toast-messages";

export type ImportCatalogItemsDrawerProps = {
  open: boolean;
  catalogName: string;
  onClose: () => void;
  onImported?: () => void;
};

function ImportCatalogItemsDrawer({
  open,
  catalogName,
  onClose,
  onImported
}: ImportCatalogItemsDrawerProps) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importSummary, setImportSummary] =
    useState<DesignScopeImportLogStats | null>(null);

  const { importCatalog, importingCatalog } = useBootstrapDesignScopeApis();

  const isImportInProgress = importingCatalog;
  const showSummary = Boolean(importSummary);

  const resetFormState = () => {
    setFileList([]);
    setImportSummary(null);
  };

  useEffect(() => {
    if (open) return;
    resetFormState();
  }, [open]);

  const selectedFile = fileList[0];
  const canImport =
    Boolean(selectedFile && catalogName.trim()) &&
    !isImportInProgress &&
    !showSummary;

  const uploadProps: UploadProps = useMemo(
    () => ({
      accept: ".csv,text/csv",
      multiple: false,
      maxCount: 1,
      showUploadList: false,
      beforeUpload: (file) => {
        const isCsv =
          file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
        if (!isCsv) {
          message.error(DESIGN_SCOPE_CSV_FILE_ERROR);
          return Upload.LIST_IGNORE;
        }
        setFileList([
          {
            uid: file.uid,
            name: file.name,
            status: "done",
            originFileObj: file
          }
        ]);
        return false;
      },
      onRemove: () => {
        setFileList([]);
      }
    }),
    []
  );

  const handleClose = () => {
    if (isImportInProgress) return;
    onClose();
  };

  const handleBackToUpload = () => {
    setImportSummary(null);
  };

  const handleImport = async () => {
    if (!canImport || !selectedFile?.originFileObj) return;

    const name = catalogName.trim();
    setImportSummary(null);

    const result = await importCatalog({
      file: selectedFile.originFileObj as File,
      catalogName: name
    });

    if (!result.success || !result.data) return;

    onImported?.();

    if (catalogImportHasRowFailures(result.data)) {
      const stats = catalogImportSummaryStats(result.data);
      setImportSummary(stats);
      maybeDownloadImportFailureReport(stats);
      return;
    }

    message.success(DESIGN_SCOPE_ITEMS_IMPORTED);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Import List"
      size={480}
      destroyOnHidden
      closable={!isImportInProgress}
      mask={{ closable: !isImportInProgress }}
      keyboard={!isImportInProgress}
      className="bootstrap-ds-import-drawer catalog-items-import-drawer"
      data-testid="catalog-items-import-drawer"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleClose}
            disabled={isImportInProgress}
            data-testid="catalog-items-import-cancel"
          >
            {showSummary ? "Close" : "Cancel"}
          </Button>
          {!isImportInProgress && !showSummary && (
            <Button
              type="primary"
              className="bg-ciq-primary-800 border-ciq-primary-800"
              disabled={!canImport}
              loading={importingCatalog}
              onClick={() => void handleImport()}
              data-testid="catalog-items-import-submit"
            >
              Import
            </Button>
          )}
        </div>
      }
    >
      {showSummary && importSummary ? (
        <ImportLogSummaryPanel
          stats={importSummary}
          description="Design Scope catalog import complete. Review the summary below."
          onBack={handleBackToUpload}
          backLabel="Back to upload"
        />
      ) : (
        <div className="flex flex-col gap-5">
          <section data-testid="catalog-items-import-upload">
            <div className="font-semibold text-ciq-gray-950">
              Import to update the list.
            </div>
            <div className="mt-1 text-xs text-black text-opacity-50">
              The import file should match the format of the exported csv.
            </div>
            <div className="mt-3">
              <Dragger
                {...uploadProps}
                disabled={isImportInProgress}
                data-testid="catalog-items-import-dragger"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to upload</p>
                <p className="ant-upload-hint">Only CSV files are supported</p>
              </Dragger>
            </div>
          </section>

          <div
            className="catalog-items-import-note flex items-start gap-3 rounded-lg px-4 py-3 text-sm leading-5"
            data-testid="catalog-items-import-match-note"
          >
            <InfoCircleOutlined className="mt-0.5 shrink-0" />
            <span>
              Items are matched by Title: new items will be added, and existing
              ones will be updated
            </span>
          </div>

          {selectedFile && (
            <section
              className="rounded border border-solid border-ciq-gray-200 p-3"
              data-testid="catalog-items-import-file"
            >
              <div className="flex items-center gap-2 text-sm font-medium text-ciq-gray-950">
                <PaperClipOutlined />
                <span className="truncate" title={selectedFile.name}>
                  {selectedFile.name}
                </span>
              </div>
            </section>
          )}

          {isImportInProgress && (
            <div
              className="flex flex-col items-center gap-2 py-4"
              data-testid="catalog-items-import-processing"
            >
              <Spin size="medium" />
              <p className="m-0 text-sm font-semibold">Import in progress</p>
              <p className="m-0 text-center text-xs text-black text-opacity-50">
                Import may take a couple of minutes. Please remain on this page
                until import is complete
              </p>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

export default ImportCatalogItemsDrawer;
